import React, { useState, useRef, useEffect } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  Alert,
  Image,
  Pressable,
  ScrollView,
  FlatList,
  Keyboard,
  Platform,
} from 'react-native';
import Modal from 'react-native-modal';
import { useTranslation } from 'react-i18next';
import { useAtom } from 'jotai';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { useRouter } from 'expo-router';
import { Feather } from '@expo/vector-icons';
import * as ImagePicker from 'expo-image-picker';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useRequireAuth } from '../hooks/useRequireAuth';
import { createPostSheetVisibleAtom, selectedGroupAtom } from '../atoms';
import { insertPost } from '../services/postService';
import { fetchGroups } from '../services/groupService';
import { uploadImage } from '../utils/firebaseImages';
import { uploadToR2, uploadFromUri } from '../services/r2Storage';
import {
  validateVideoFile,
  compressVideo,
  getVideoMetadata,
  CompressionProgress,
} from '../utils/videoCompression';
import { Group, ZONE_TYPE_INFO } from '../types';
import CachedImage from './CachedImage';
import PixelContainer from './PixelContainer';
import { fontStyle } from '../config/fonts';

export default function CreatePostBottomSheet() {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const { user } = useAuth();
  const { requireAuth } = useRequireAuth();
  const queryClient = useQueryClient();
  const router = useRouter();
  const [isVisible, setIsVisible] = useAtom(createPostSheetVisibleAtom);
  const [selectedGroup, setSelectedGroup] = useAtom(selectedGroupAtom);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [imageUris, setImageUris] = useState<string[]>([]);
  const [videoUri, setVideoUri] = useState<string | null>(null);
  const [compressedVideoUri, setCompressedVideoUri] = useState<string | null>(null);
  const [compressionProgress, setCompressionProgress] = useState<CompressionProgress | null>(null);
  const [isCompressing, setIsCompressing] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [isUploading, setIsUploading] = useState(false);
  const [link, setLink] = useState('');
  const MAX_IMAGES = 10;
  const [showZoneSelector, setShowZoneSelector] = useState(false);
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [zoneSearch, setZoneSearch] = useState('');

  const titleInputRef = useRef<TextInput>(null);
  const linkInputRef = useRef<TextInput>(null);

  // Security check: Close sheet if user is not authenticated
  useEffect(() => {
    if (isVisible && !user) {
      // User is not authenticated, close the sheet immediately
      setIsVisible(false);
      resetForm();
    }
  }, [isVisible, user]);

  // Auto-focus title input when sheet opens
  useEffect(() => {
    if (isVisible && !showZoneSelector && user) {
      // Small delay to ensure modal is fully visible
      const timer = setTimeout(() => {
        titleInputRef.current?.focus();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isVisible, showZoneSelector, user]);

  // Fetch zones for selector
  const { data: zones } = useQuery({
    queryKey: ['groups', zoneSearch],
    queryFn: () => fetchGroups({ search: zoneSearch }),
    enabled: showZoneSelector,
    staleTime: 30000,
  });

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setImageUris([]);
    setVideoUri(null);
    setCompressedVideoUri(null);
    setCompressionProgress(null);
    setIsCompressing(false);
    setUploadProgress(0);
    setIsUploading(false);
    setLink('');
    setSelectedGroup(null);
    setShowZoneSelector(false);
    setShowLinkInput(false);
    setZoneSearch('');
  };

  const handleClose = () => {
    resetForm();
    setIsVisible(false);
  };

  const postMutation = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error('Not authenticated');
      if (!selectedGroup) throw new Error('No zone selected');

      let imageUrls: string[] | undefined;
      let videoUrl: string | undefined;

      // Upload compressed video (mutually exclusive with images)
      if (compressedVideoUri) {
        console.log('[Video] Starting upload, compressedVideoUri:', compressedVideoUri);
        setIsUploading(true);

        try {
          if (Platform.OS === 'web') {
            // Web: convert blob URL to File
            const response = await fetch(compressedVideoUri);
            const blob = await response.blob();
            const file = new File([blob], 'video.mp4', { type: 'video/mp4' });

            videoUrl = await uploadToR2(file, 'videos', (progress) => {
              setUploadProgress(progress.percentage);
            });
          } else {
            // Native: upload directly from file URI
            videoUrl = await uploadFromUri(compressedVideoUri, 'video/mp4', 'videos', (progress) => {
              setUploadProgress(progress.percentage);
            });
          }
          console.log('[Video] Upload complete, videoUrl:', videoUrl);
        } catch (uploadError) {
          console.error('[Video] Upload failed:', uploadError);
          throw uploadError;
        }
      } else if (imageUris.length > 0) {
        // Upload all images in parallel
        imageUrls = await Promise.all(imageUris.map(uri => uploadImage(uri)));
      }

      const postData = {
        title: title.trim(),
        description: description.trim() || undefined,
        images: imageUrls,
        video: videoUrl,
        link: link.trim() || undefined,
        groupId: selectedGroup.id,
      };
      console.log('[Post] Creating post with data:', postData);

      return insertPost(postData, user.uid);
    },
    onSuccess: (postId) => {
      queryClient.invalidateQueries({ queryKey: ['posts'] });
      queryClient.invalidateQueries({ queryKey: ['zone-posts', selectedGroup?.id] });
      handleClose();
      // Navigate to the new post
      router.push(`/post/${postId}`);
    },
    onError: (error) => {
      Alert.alert(t('common.error'), t('errors.postFailed'));
      console.error('Post creation error:', error);
    },
  });

  const handleCreate = () => {
    if (!requireAuth()) return;

    if (!selectedGroup) {
      Alert.alert(t('common.error'), t('post.selectZone'));
      return;
    }

    if (!title.trim()) {
      Alert.alert(t('common.error'), t('post.title'));
      return;
    }

    postMutation.mutate();
  };

  const handlePickImage = async () => {
    if (imageUris.length >= MAX_IMAGES) {
      Alert.alert(t('common.error'), t('post.maxImages', { count: MAX_IMAGES }));
      return;
    }

    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ['images'],
      allowsEditing: false,
      allowsMultipleSelection: true,
      selectionLimit: MAX_IMAGES - imageUris.length,
      quality: 1, // We'll compress later in the upload process
    });

    if (!result.canceled && result.assets.length > 0) {
      const newUris = result.assets.map(asset => asset.uri);
      setImageUris(prev => [...prev, ...newUris].slice(0, MAX_IMAGES));
      // Clear video since images are mutually exclusive with video
      setVideoUri(null);
      setCompressedVideoUri(null);
      setCompressionProgress(null);
      setIsCompressing(false);
    }
  };

  const handleRemoveImage = (index: number) => {
    setImageUris(prev => prev.filter((_, i) => i !== index));
  };

  const handlePickVideo = async () => {
    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ['videos'],
      allowsEditing: false,
      quality: 1,
    });

    if (!result.canceled && result.assets.length > 0) {
      const asset = result.assets[0];
      setVideoUri(asset.uri);
      // Clear images since video is mutually exclusive
      setImageUris([]);
      // Reset compression state
      setCompressedVideoUri(null);
      setCompressionProgress(null);

      try {
        // On web, use asset.file (the actual File object) since blob URLs are revoked immediately
        // On native, use asset.uri as usual
        const videoInput = Platform.OS === 'web' && (asset as any).file
          ? (asset as any).file
          : asset.uri;

        // Get metadata for validation
        const metadata = await getVideoMetadata(videoInput);

        // Get file size - use asset.fileSize or file.size if available, otherwise estimate
        let fileSize = asset.fileSize || ((asset as any).file?.size) || 0;

        // On native, if fileSize not available, try to get it
        if (!fileSize && Platform.OS !== 'web') {
          try {
            const response = await fetch(asset.uri);
            const blob = await response.blob();
            fileSize = blob.size;
          } catch {
            // Use a default that will pass validation
            fileSize = 0;
          }
        }

        // Validate video (skip if fileSize is 0)
        if (fileSize > 0) {
          const validation = validateVideoFile(fileSize, metadata.duration);
          if (!validation.valid) {
            Alert.alert(t('common.error'), validation.error || t('errors.postFailed'));
            setVideoUri(null);
            return;
          }
        }

        // Start compression immediately
        setIsCompressing(true);
        const compressed = await compressVideo(videoInput, setCompressionProgress);
        console.log('[Video] Compression complete:', compressed.uri);
        setCompressedVideoUri(compressed.uri);
        setIsCompressing(false);
      } catch (error) {
        console.error('Video compression error:', error);
        Alert.alert(t('common.error'), t('errors.postFailed'));
        setVideoUri(null);
        setIsCompressing(false);
      }
    }
  };

  const handleRemoveVideo = () => {
    setVideoUri(null);
    setCompressedVideoUri(null);
    setCompressionProgress(null);
    setIsCompressing(false);
    setUploadProgress(0);
  };

  const handleSelectZone = (zone: Group) => {
    setSelectedGroup(zone);
    setShowZoneSelector(false);
    setZoneSearch('');
  };

  const handleSaveLink = () => {
    setShowLinkInput(false);
  };

  const handleRemoveLink = () => {
    setLink('');
  };

  const typeInfo = selectedGroup ? ZONE_TYPE_INFO[selectedGroup.type] : null;
  const isVideoNotReady = Boolean(videoUri && !compressedVideoUri);
  const isPostDisabled = !title.trim() || !selectedGroup || postMutation.isPending || isCompressing || isVideoNotReady;

  // Helper to get link type
  const getLinkType = (url: string): 'youtube' | 'twitter' | 'other' | null => {
    if (!url) return null;
    if (url.includes('youtube.com') || url.includes('youtu.be')) return 'youtube';
    if (url.includes('twitter.com') || url.includes('x.com')) return 'twitter';
    return 'other';
  };

  const linkType = getLinkType(link);

  // Don't render anything if user is not authenticated (security check)
  if (!user) {
    return null;
  }

  const renderZoneItem = ({ item: zone }: { item: Group }) => {
    const zoneTypeInfo = ZONE_TYPE_INFO[zone.type];
    return (
      <Pressable
        style={[styles.zoneItem, { borderBottomColor: colors.border }]}
        onPress={() => handleSelectZone(zone)}
      >
        <View style={styles.zoneImageContainer}>
          {zone.image ? (
            <CachedImage uri={zone.image} style={styles.zoneImage} />
          ) : (
            <View style={[styles.zoneImage, styles.zoneImagePlaceholder, { backgroundColor: colors.surface }]}>
              <Feather name="users" size={18} color={colors.textSecondary} />
            </View>
          )}
          <View style={[styles.typeBadge, { backgroundColor: zoneTypeInfo.color }]}>
            <Text style={styles.typeBadgeEmoji}>{zoneTypeInfo.emoji}</Text>
          </View>
        </View>
        <View style={styles.zoneInfo}>
          <Text style={[styles.zoneName, fontStyle, { color: colors.text }]}>{zone.name}</Text>
          <Text style={[styles.zoneMeta, fontStyle, { color: colors.textSecondary }]}>
            {zone.membersCount || 0} {(zone.membersCount || 0) === 1 ? t('zone.member') : t('zone.members')}
          </Text>
        </View>
      </Pressable>
    );
  };

  // Link Input Modal
  if (showLinkInput) {
    return (
      <Modal
        isVisible={isVisible}
        onBackdropPress={() => setShowLinkInput(false)}
        style={styles.modal}
        backdropOpacity={0.5}
        animationIn="slideInUp"
        animationOut="slideOutDown"
        avoidKeyboard
        customBackdrop={
          <Pressable
            style={[
              styles.backdrop,
              Platform.OS === 'web' && {
                position: 'fixed' as any,
                width: '100vw' as any,
                height: '100vh' as any,
              }
            ]}
            onPress={() => setShowLinkInput(false)}
          />
        }
      >
        <View style={[styles.linkInputContent, { backgroundColor: colors.background }]}>
          <View style={[styles.indicator, { backgroundColor: colors.border }]} />

          {/* Header */}
          <View style={styles.header}>
            <Pressable onPress={() => setShowLinkInput(false)} style={styles.headerButton}>
              <Text style={[styles.cancelText, fontStyle, { color: colors.textSecondary }]}>
                {t('common.cancel')}
              </Text>
            </Pressable>
            <Text style={[styles.headerTitle, fontStyle, { color: colors.text }]}>
              {t('post.addLink')}
            </Text>
            <Pressable onPress={handleSaveLink}>
              <PixelContainer
                backgroundColor={link.trim() ? colors.primary : colors.surface}
                cornerSize={4}
                style={[styles.postButton, !link.trim() && styles.postButtonDisabled]}
              >
                <Text style={[styles.postButtonText, fontStyle]}>
                  {t('common.done')}
                </Text>
              </PixelContainer>
            </Pressable>
          </View>

          <View style={styles.linkInputBody}>
            <Text style={[styles.linkInputLabel, fontStyle, { color: colors.textSecondary }]}>
              {t('post.linkPlaceholder')}
            </Text>
            <View style={[styles.linkInputWrapper, { backgroundColor: colors.surface }]}>
              <TextInput
                ref={linkInputRef}
                style={[styles.linkInput, fontStyle, { color: colors.text }]}
                placeholder="https://youtube.com/watch?v=..."
                placeholderTextColor={colors.textSecondary}
                value={link}
                onChangeText={setLink}
                autoCapitalize="none"
                autoCorrect={false}
                keyboardType="url"
                autoFocus
              />
            </View>
            <Text style={[styles.linkInputHint, fontStyle, { color: colors.textSecondary }]}>
              {t('post.linkHint')}
            </Text>
          </View>
        </View>
      </Modal>
    );
  }

  // Zone Selector View
  if (showZoneSelector) {
    return (
      <Modal
        isVisible={isVisible}
        onBackdropPress={handleClose}
        onSwipeComplete={handleClose}
        swipeDirection={['down']}
        style={styles.modal}
        backdropOpacity={0.5}
        animationIn="slideInUp"
        animationOut="slideOutDown"
        avoidKeyboard
        customBackdrop={
          <Pressable
            style={[
              styles.backdrop,
              Platform.OS === 'web' && {
                position: 'fixed' as any,
                width: '100vw' as any,
                height: '100vh' as any,
              }
            ]}
            onPress={handleClose}
          />
        }
      >
        <View style={[styles.content, styles.zoneSelectorContent, { backgroundColor: colors.background }]}>
          <View style={[styles.indicator, { backgroundColor: colors.border }]} />

          {/* Header */}
          <View style={styles.zoneSelectorHeader}>
            <Pressable onPress={() => setShowZoneSelector(false)} style={styles.backButton}>
              <Feather name="arrow-left" size={22} color={colors.text} />
            </Pressable>
            <Text style={[styles.title, fontStyle, { color: colors.text }]}>{t('post.postTo')}</Text>
            <View style={styles.backButton} />
          </View>

          {/* Search */}
          <View style={[styles.searchContainer, { backgroundColor: colors.surface }]}>
            <Feather name="search" size={18} color={colors.textSecondary} />
            <TextInput
              style={[styles.searchInput, fontStyle, { color: colors.text }]}
              placeholder={t('zone.search')}
              placeholderTextColor={colors.textSecondary}
              value={zoneSearch}
              onChangeText={setZoneSearch}
              autoFocus
            />
            {zoneSearch.length > 0 && (
              <Pressable onPress={() => setZoneSearch('')}>
                <Feather name="x-circle" size={16} color={colors.textSecondary} />
              </Pressable>
            )}
          </View>

          {/* Zones List */}
          <FlatList
            data={zones || []}
            renderItem={renderZoneItem}
            keyExtractor={(item) => item.id}
            style={styles.zonesList}
            contentContainerStyle={styles.zonesListContent}
            showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps="handled"
            keyboardDismissMode="none"
            ListEmptyComponent={
              <View style={styles.emptyZones}>
                <Text style={[styles.emptyText, fontStyle, { color: colors.textSecondary }]}>{t('zone.noZones')}</Text>
              </View>
            }
          />
        </View>
      </Modal>
    );
  }

  // Main Create Post View
  return (
    <Modal
      isVisible={isVisible}
      onBackdropPress={handleClose}
      onSwipeComplete={handleClose}
      swipeDirection={['down']}
      style={styles.modal}
      backdropOpacity={0.5}
      animationIn="slideInUp"
      animationOut="slideOutDown"
      propagateSwipe
      avoidKeyboard
      customBackdrop={
        <Pressable
          style={[
            styles.backdrop,
            Platform.OS === 'web' && {
              position: 'fixed' as any,
              width: '100vw' as any,
              height: '100vh' as any,
            }
          ]}
          onPress={handleClose}
        />
      }
    >
      <View style={[styles.content, { backgroundColor: colors.background }]}>
        <View style={[styles.indicator, { backgroundColor: colors.border }]} />

        {/* Header */}
        <View style={styles.header}>
          <Pressable onPress={handleClose} style={styles.headerButton}>
            <Text style={[styles.cancelText, fontStyle, { color: colors.textSecondary }]}>
              {t('common.cancel')}
            </Text>
          </Pressable>
          <Text style={[styles.headerTitle, fontStyle, { color: colors.text }]}>
            {t('tabs.create')}
          </Text>
          <Pressable onPress={handleCreate} disabled={isPostDisabled}>
            <PixelContainer
              backgroundColor={isPostDisabled ? colors.surface : colors.primary}
              cornerSize={4}
              style={[styles.postButton, isPostDisabled && styles.postButtonDisabled]}
            >
              <Text style={[styles.postButtonText, fontStyle]}>
                {postMutation.isPending ? t('post.posting') : t('post.post')}
              </Text>
            </PixelContainer>
          </Pressable>
        </View>

        <ScrollView
          style={styles.scrollView}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
        >
          {/* Zone Selector */}
          <Pressable onPress={() => setShowZoneSelector(true)}>
            <PixelContainer backgroundColor={colors.surface} cornerSize={4} style={styles.zoneSelector}>
              {selectedGroup ? (
                <View style={styles.selectedZone}>
                  <View style={styles.selectedZoneImageContainer}>
                    {selectedGroup.image ? (
                      <CachedImage uri={selectedGroup.image} style={styles.selectedZoneImage} />
                    ) : (
                      <View style={[styles.selectedZoneImage, styles.zoneImagePlaceholder, { backgroundColor: colors.border }]}>
                        <Feather name="users" size={14} color={colors.textSecondary} />
                      </View>
                    )}
                    {typeInfo && (
                      <View style={[styles.selectedTypeBadge, { backgroundColor: typeInfo.color }]}>
                        <Text style={styles.selectedTypeBadgeEmoji}>{typeInfo.emoji}</Text>
                      </View>
                    )}
                  </View>
                  <Text style={[styles.selectedZoneName, fontStyle, { color: colors.text }]} numberOfLines={1}>
                    {selectedGroup.name}
                  </Text>
                </View>
              ) : (
                <View style={styles.zonePlaceholderRow}>
                  <Feather name="users" size={18} color={colors.textSecondary} />
                  <Text style={[styles.zonePlaceholder, fontStyle, { color: colors.textSecondary }]}>
                    {t('post.selectZone')}
                  </Text>
                </View>
              )}
              <Feather name="chevron-right" size={20} color={colors.textSecondary} />
            </PixelContainer>
          </Pressable>

          {/* Title Input */}
          <View style={[styles.titleInputContainer, { backgroundColor: colors.surface }]}>
            <TextInput
              ref={titleInputRef}
              style={[styles.titleInput, fontStyle, { color: colors.text }]}
              placeholder={t('post.titlePlaceholder')}
              placeholderTextColor={colors.textSecondary}
              value={title}
              onChangeText={setTitle}
              multiline
              maxLength={300}
            />
          </View>

          {/* Description Input */}
          <View style={[styles.descriptionInputContainer, { backgroundColor: colors.surface }]}>
            <TextInput
              style={[styles.descriptionInput, fontStyle, { color: colors.text }]}
              placeholder={t('post.bodyPlaceholder')}
              placeholderTextColor={colors.textSecondary}
              value={description}
              onChangeText={setDescription}
              multiline
            />
          </View>

          {/* Video Preview */}
          {videoUri && (
            <View style={styles.videoPreviewContainer}>
              <View style={[styles.videoThumbnailWrapper, { backgroundColor: colors.surface }]}>
                <Feather name="video" size={32} color={colors.primary} />
                <Text style={[styles.videoLabel, fontStyle, { color: colors.text }]}>
                  {isCompressing
                    ? t('post.compressingVideo')
                    : compressedVideoUri
                      ? t('post.videoReady')
                      : t('post.videoSelected')}
                </Text>
                {/* Compression progress */}
                {isCompressing && compressionProgress && (
                  <View style={styles.uploadProgressContainer}>
                    <View style={[styles.uploadProgressBar, { backgroundColor: colors.border }]}>
                      <View
                        style={[
                          styles.uploadProgressFill,
                          { backgroundColor: colors.primary, width: `${compressionProgress.progress}%` }
                        ]}
                      />
                    </View>
                    <Text style={[styles.uploadProgressText, fontStyle, { color: colors.textSecondary }]}>
                      {compressionProgress.stage === 'loading'
                        ? t('post.loadingCompressor')
                        : compressionProgress.stage === 'finalizing'
                          ? t('post.finalizingVideo')
                          : `${t('post.compressing')} ${compressionProgress.progress}%`}
                    </Text>
                  </View>
                )}
                {/* Upload progress */}
                {isUploading && (
                  <View style={styles.uploadProgressContainer}>
                    <View style={[styles.uploadProgressBar, { backgroundColor: colors.border }]}>
                      <View
                        style={[
                          styles.uploadProgressFill,
                          { backgroundColor: colors.accent, width: `${uploadProgress}%` }
                        ]}
                      />
                    </View>
                    <Text style={[styles.uploadProgressText, fontStyle, { color: colors.textSecondary }]}>
                      {t('post.uploadingVideo')} {uploadProgress}%
                    </Text>
                  </View>
                )}
                {/* Compressed indicator */}
                {compressedVideoUri && !isCompressing && !isUploading && (
                  <View style={styles.compressedBadge}>
                    <Feather name="check-circle" size={16} color={colors.accent} />
                  </View>
                )}
                {!isCompressing && !isUploading && (
                  <Pressable
                    onPress={handleRemoveVideo}
                    style={[styles.removeVideoButton, { backgroundColor: colors.error }]}
                  >
                    <Feather name="x" size={14} color="#fff" />
                  </Pressable>
                )}
              </View>
            </View>
          )}

          {/* Image Previews - Small square thumbnails */}
          {imageUris.length > 0 && (
            <View style={styles.imagePreviewContainer}>
              <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.imagePreviewScroll}>
                {imageUris.map((uri, index) => (
                  <View key={uri} style={styles.imageThumbnailWrapper}>
                    <Image source={{ uri }} style={styles.imageThumbnail} resizeMode="cover" />
                    <Pressable
                      onPress={() => handleRemoveImage(index)}
                      style={[styles.removeThumbnail, { backgroundColor: colors.error }]}
                    >
                      <Feather name="x" size={12} color="#fff" />
                    </Pressable>
                  </View>
                ))}
                {imageUris.length < MAX_IMAGES && (
                  <Pressable onPress={handlePickImage} style={[styles.addMoreThumbnail, { backgroundColor: colors.surface, borderColor: colors.border }]}>
                    <Feather name="plus" size={24} color={colors.textSecondary} />
                  </Pressable>
                )}
              </ScrollView>
            </View>
          )}

          {/* Link Preview */}
          {link && (
            <View style={[styles.linkPreview, { backgroundColor: colors.surface }]}>
              <View style={styles.linkPreviewContent}>
                <Feather name="link" size={16} color={colors.primary} />
                <Text style={[styles.linkPreviewText, fontStyle, { color: colors.text }]} numberOfLines={1}>
                  {linkType === 'youtube' && '▶ '}
                  {linkType === 'twitter' && '𝕏 '}
                  {link}
                </Text>
              </View>
              <Pressable onPress={handleRemoveLink}>
                <Feather name="x" size={16} color={colors.textSecondary} />
              </Pressable>
            </View>
          )}

          <View style={styles.bottomSpacer} />
        </ScrollView>

        {/* Footer Actions */}
        <View style={[styles.footer, { borderTopColor: colors.border }]}>
          <Pressable
            onPress={handlePickImage}
            style={[styles.footerButton, { backgroundColor: imageUris.length > 0 ? colors.primary : colors.surface }]}
          >
            <Feather name="image" size={20} color={imageUris.length > 0 ? '#fff' : colors.text} />
            {imageUris.length > 0 && (
              <View style={[styles.footerButtonBadge, { backgroundColor: colors.accent }]}>
                <Text style={styles.footerButtonBadgeText}>{imageUris.length}</Text>
              </View>
            )}
          </Pressable>
          <Pressable
            onPress={handlePickVideo}
            style={[styles.footerButton, { backgroundColor: videoUri ? colors.primary : colors.surface }]}
          >
            <Feather name="video" size={20} color={videoUri ? '#fff' : colors.text} />
          </Pressable>
          <Pressable
            onPress={() => setShowLinkInput(true)}
            style={[styles.footerButton, { backgroundColor: link ? colors.primary : colors.surface }]}
          >
            <Feather name="link" size={20} color={link ? '#fff' : colors.text} />
          </Pressable>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  modal: {
    justifyContent: 'flex-end',
    margin: 0,
  },
  backdrop: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
  },
  content: {
    maxHeight: '90%',
    paddingTop: 12,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
  },
  zoneSelectorContent: {
    height: '70%',
  },
  indicator: {
    width: 40,
    height: 4,
    borderRadius: 2,
    alignSelf: 'center',
    marginBottom: 12,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingBottom: 12,
  },
  headerButton: {
    paddingVertical: 8,
  },
  headerTitle: {
    fontSize: 17,
    fontWeight: '600',
  },
  cancelText: {
    fontSize: 16,
  },
  postButton: {
    paddingHorizontal: 18,
    paddingVertical: 8,
  },
  postButtonDisabled: {
    opacity: 0.5,
  },
  postButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },
  scrollView: {
    paddingHorizontal: 16,
  },
  zoneSelector: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 12,
    paddingHorizontal: 14,
    marginBottom: 20,
  },
  selectedZone: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  selectedZoneImageContainer: {
    position: 'relative',
    marginRight: 10,
  },
  selectedZoneImage: {
    width: 32,
    height: 32,
    borderRadius: 16,
  },
  selectedTypeBadge: {
    position: 'absolute',
    bottom: -2,
    right: -2,
    width: 14,
    height: 14,
    borderRadius: 7,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1.5,
    borderColor: '#fff',
  },
  selectedTypeBadgeEmoji: {
    fontSize: 6,
  },
  selectedZoneName: {
    fontSize: 15,
    fontWeight: '500',
    flex: 1,
    flexShrink: 1,
  },
  zonePlaceholderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    flex: 1,
  },
  zonePlaceholder: {
    fontSize: 15,
  },
  titleInputContainer: {
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    marginBottom: 12,
  },
  titleInput: {
    fontSize: 22,
    fontWeight: '600',
    lineHeight: 30,
  },
  descriptionInputContainer: {
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    marginBottom: 12,
  },
  descriptionInput: {
    fontSize: 16,
    lineHeight: 24,
    minHeight: 100,
  },
  imagePreviewContainer: {
    marginTop: 12,
    marginBottom: 8,
  },
  imagePreviewScroll: {
    gap: 8,
  },
  imageThumbnailWrapper: {
    position: 'relative',
    width: 72,
    height: 72,
    borderRadius: 8,
    overflow: 'hidden',
  },
  imageThumbnail: {
    width: '100%',
    height: '100%',
  },
  removeThumbnail: {
    position: 'absolute',
    top: 4,
    right: 4,
    width: 20,
    height: 20,
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
  },
  addMoreThumbnail: {
    width: 72,
    height: 72,
    borderRadius: 8,
    borderWidth: 2,
    borderStyle: 'dashed',
    justifyContent: 'center',
    alignItems: 'center',
  },
  videoPreviewContainer: {
    marginTop: 12,
    marginBottom: 8,
  },
  videoThumbnailWrapper: {
    position: 'relative',
    padding: 20,
    borderRadius: 8,
    alignItems: 'center',
    gap: 8,
  },
  videoLabel: {
    fontSize: 14,
    fontWeight: '500',
  },
  removeVideoButton: {
    position: 'absolute',
    top: 8,
    right: 8,
    width: 24,
    height: 24,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
  },
  uploadProgressContainer: {
    width: '100%',
    marginTop: 8,
  },
  uploadProgressBar: {
    height: 4,
    borderRadius: 2,
    overflow: 'hidden',
  },
  uploadProgressFill: {
    height: '100%',
  },
  uploadProgressText: {
    fontSize: 11,
    marginTop: 4,
    textAlign: 'center',
  },
  compressedBadge: {
    marginTop: 4,
  },
  footer: {
    flexDirection: 'row',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderTopWidth: 1,
    gap: 12,
  },
  footerButton: {
    width: 44,
    height: 44,
    borderRadius: 22,
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
  },
  footerButtonBadge: {
    position: 'absolute',
    top: -4,
    right: -4,
    minWidth: 18,
    height: 18,
    borderRadius: 9,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 4,
  },
  footerButtonBadgeText: {
    color: '#fff',
    fontSize: 10,
    fontWeight: '700',
  },
  bottomSpacer: {
    height: 20,
  },
  // Zone Selector Styles
  zoneSelectorHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingBottom: 12,
  },
  backButton: {
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  title: {
    fontSize: 17,
    fontWeight: '600',
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginHorizontal: 16,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 10,
    gap: 8,
  },
  searchInput: {
    flex: 1,
    fontSize: 15,
  },
  zonesList: {
    flex: 1,
    marginTop: 8,
  },
  zonesListContent: {
    paddingHorizontal: 16,
    paddingBottom: 20,
  },
  zoneItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    borderBottomWidth: 1,
  },
  zoneImageContainer: {
    position: 'relative',
    marginRight: 12,
  },
  zoneImage: {
    width: 44,
    height: 44,
    borderRadius: 22,
  },
  zoneImagePlaceholder: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  typeBadge: {
    position: 'absolute',
    bottom: -2,
    right: -2,
    width: 18,
    height: 18,
    borderRadius: 9,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 2,
    borderColor: '#fff',
  },
  typeBadgeEmoji: {
    fontSize: 8,
  },
  zoneInfo: {
    flex: 1,
  },
  zoneName: {
    fontSize: 15,
    fontWeight: '600',
    marginBottom: 2,
  },
  zoneMeta: {
    fontSize: 12,
  },
  emptyZones: {
    paddingVertical: 40,
    alignItems: 'center',
  },
  emptyText: {
    fontSize: 15,
  },
  linkPreview: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 12,
    borderRadius: 8,
    marginBottom: 12,
  },
  linkPreviewContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flex: 1,
  },
  linkPreviewText: {
    fontSize: 13,
    flex: 1,
  },
  linkInputContent: {
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    paddingTop: 12,
    paddingBottom: 40,
  },
  linkInputBody: {
    paddingHorizontal: 16,
    paddingTop: 20,
  },
  linkInputLabel: {
    fontSize: 14,
    fontWeight: '500',
    marginBottom: 8,
  },
  linkInputWrapper: {
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 12,
  },
  linkInput: {
    fontSize: 15,
  },
  linkInputHint: {
    fontSize: 12,
    marginTop: 8,
  },
});
